import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchMyModels } from '../marketplace/marketplaceApi';
import type { MarketplaceModel, AuthHeadersFn } from '../marketplace/marketplaceTypes';
import {
  buildLayers, activeLayer, feedChannel,
  type LayerRow, type LayerId, type OverlayState,
} from './aiLayers';
import type { ServerChannel, FeedSource } from './aiChannels';

/**
 * How often to re-read the server's overlay state.
 *
 * The active model is a process-global, so two dashboards were always able to
 * contradict each other; this is what makes another operator's switch show up.
 */
export const OVERLAY_POLL_MS = 10000;

interface Options {
  /** Off before login, and while the video panel is closed — nothing to poll for. */
  enabled: boolean;
  /** Skip the poll while a switch is in flight, so it cannot clobber the write. */
  busy: boolean;
  /** Called with each fresh reading, for the owner to mirror into its own state. */
  onState?: (state: OverlayState) => void;
  /**
   * The feed the picker is showing, so it can answer for THAT feed.
   *
   * Without it the picker reports the process-global primary state whatever is
   * on screen, which on a feed carrying its own channel means reporting `Off`
   * over a running model — see `feedChannel`.
   */
  feed?: FeedSource | null;
}

interface Result {
  rows: LayerRow[];
  /** Null until the first reading lands, or if the endpoint is unreachable. */
  state: OverlayState | null;
  active: LayerId;
  /**
   * The channel standing on `feed`, when one does. Non-null means the selection
   * has to be written through the channel API — the primary endpoints cannot
   * reach it, which is why turning such a model off used to do nothing.
   */
  channel: ServerChannel | null;
  /** Re-read now — call after a switch so the menu reflects what actually happened. */
  refresh: () => Promise<void>;
}

/**
 * Feeds the AI layer picker: what layers exist, and which one is running.
 *
 * Both halves degrade to something usable rather than throwing. A node with no
 * marketplace catalogue still gets a detection row (see `buildLayers`), and an
 * unreachable overlay endpoint just leaves the menu showing Off rather than
 * blocking the operator from selecting anything.
 */
export function useAiLayers(apiUrl: string, authHeaders: AuthHeadersFn, opts: Options): Result {
  // null means 'could not ask', which buildLayers treats differently from 'none'.
  const [models, setModels] = useState<MarketplaceModel[] | null>(null);
  const [state, setState] = useState<OverlayState | null>(null);

  // Read inside the poll without making them dependencies — either would restart
  // the interval on every render.
  const busyRef = useRef(opts.busy);
  busyRef.current = opts.busy;
  const { enabled, feed } = opts;
  const onStateRef = useRef(opts.onState);
  onStateRef.current = opts.onState;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchMyModels(authHeaders);
        if (!cancelled) setModels(list);
      } catch {
        // Leave it null: the catalogue is absent on nodes that never migrated,
        // and the fallback detection row covers them.
        if (!cancelled) setModels(null);
      }
    })();
    return () => { cancelled = true; };
  }, [authHeaders, enabled]);

  const read = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/drone/overlays`, { headers: authHeaders() });
      if (!res.ok) return;
      const data: OverlayState = await res.json();
      setState(data);
      onStateRef.current?.(data);
    } catch {
      /* transient — the next tick retries */
    }
  }, [apiUrl, authHeaders]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const tick = async () => {
      if (busyRef.current || cancelled) return;
      await read();
    };
    tick();
    const timer = setInterval(tick, OVERLAY_POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [read, enabled]);

  // App.tsx re-renders on every map interaction and passes `rows` straight down;
  // a fresh array each time would churn the menu for no reason.
  const rows = useMemo(() => buildLayers(models), [models]);

  const channel = useMemo(() => feedChannel(state, feed), [state, feed]);

  return { rows, state, active: activeLayer(state, feed), channel, refresh: read };
}
